import styled from '@emotion/styled';
import dayjs from 'lib/dayjs';
import { memo } from 'react';
import { useTranslate } from '@tolgee/react';
import { PreviewModalProvider } from 'views/Conversation/Sider/PreviewModal';
import { useReviewContext } from '../Context';
import LazadaReviewDetail from './LazadaReviewDetail';
import ReplyDetail, { SystemMessage } from './ReplyDetail';
import { isReplyDetail } from './ReplyEditor';
import BuyerReviewDetail from './BuyerReviewDetail';
import { useChannel2Platform } from '../hooks/useChannel2Platform';

const Container = styled.div`
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  overflow: auto;
  > * {
    margin-bottom: 16px;
  }
`;

const isReviewDetail = (
  detail: Review.ReviewDetail | Review.ReplyDetail,
): detail is Review.ReviewDetail => {
  return !isReplyDetail(detail);
};

function DetailList() {
  const { t } = useTranslate();

  const { detail } = useReviewContext();
  const { channel } = detail;
  const { isShopee, isMercado, isLazada, isTikTokCommon } = useChannel2Platform()(channel);

  const renderList = [];

  if (isShopee || isMercado) {
    const { id, commentCreateTime, reviewDetails = [] } = detail;

    if (reviewDetails.length === 0) {
      renderList.push({
        time: parseInt(commentCreateTime, 10),
        node: <BuyerReviewDetail key={id} reviewDetail={detail} isMainReview />,
      });
    } else {
      let isFirst = false;
      reviewDetails.forEach((detailItem) => {
        const { id, commentCreateTime, commentType } = detailItem;
        if (commentType === 1 && isFirst === false) {
          isFirst = true;
        } else {
          isFirst = false;
        }

        renderList.push({
          time: parseInt(commentCreateTime, 10),
          node: isReviewDetail(detailItem) ? (
            <BuyerReviewDetail key={id} reviewDetail={detailItem} isBuyerFirstReview={isFirst} />
          ) : (
            <ReplyDetail key={id} replyDetail={detailItem} channel={channel} />
          ),
        });
      });
    }
  }

  if (isTikTokCommon) {
    const { id, commentCreateTime, reviewDetails = [] } = detail;
    renderList.push({
      time: parseInt(commentCreateTime, 10),
      node: <BuyerReviewDetail key={id} reviewDetail={detail} isMainReview />,
    });
    reviewDetails.forEach((detailItem) => {
      const { id, commentCreateTime } = detailItem;
      renderList.push({
        time: parseInt(commentCreateTime, 10),
        node: isReviewDetail(detailItem) ? (
          <BuyerReviewDetail
            key={id}
            reviewDetail={detailItem}
            isMainReview={false}
            isBuyerFirstReview={false}
            isTikTok
          />
        ) : (
          <ReplyDetail key={id} replyDetail={detailItem} channel={channel} />
        ),
      });
    });
  }

  if (isLazada) {
    const { id, commentCreateTime, reviewDetails = [] } = detail;

    renderList.push({
      time: parseInt(commentCreateTime, 10),
      node: <LazadaReviewDetail key={id} reviewDetail={detail} isMainReview />,
    });

    reviewDetails.forEach((detailItem) => {
      const { id, commentCreateTime } = detail;

      renderList.push({
        time: parseInt(commentCreateTime, 10),
        node: isReviewDetail(detailItem) ? (
          <LazadaReviewDetail key={id} reviewDetail={detailItem} />
        ) : (
          <ReplyDetail key={id} replyDetail={detailItem} channel={channel} />
        ),
      });
    });
  }

  const { id, status, handleName, handleTime } = detail;

  if ((status === 3 || status === 4) && handleName && handleTime) {
    const time = parseInt(handleTime, 10);

    const statusLabel = {
      3: t('review.已处理'),
      4: t('review.无需处理'),
    }[status];

    renderList.push({
      time,
      node: (
        <SystemMessage key={`${id}_status`}>
          {dayjs(time).format('YYYY-MM-DD HH:mm:ss')}&nbsp;
          {t('review.x将该评价标为y', { x: handleName, y: statusLabel })}
        </SystemMessage>
      ),
    });
  }

  return (
    <PreviewModalProvider>
      <Container>{renderList.sort((a, b) => a.time - b.time).map(({ node }) => node)}</Container>
    </PreviewModalProvider>
  );
}

export default memo(DetailList);
